/**
 * The normal Alfie in finance mode on the Xero screens (Tim, 31 Oct 2026).
 *
 * WHAT A TOPIC IS. Everything on a Xero screen that has an ✦ "Ask Alfie" button — a figure, a key
 * question, the "what's blocking my dashboard" panel, a ledger account — already has a brief that
 * answers well. A topic is that brief plus the words that name it. When the builder asks the normal
 * Alfie a question there, the question is matched to ONE topic and goes with that topic's brief:
 * the same text the button would have sent. Nothing new is written for the model.
 *
 * HOW THE SCREEN AND THE WIDGET MEET. They are mounted in different trees (the widget sits at the
 * app root), so a screen REGISTERS its topics here while it is mounted, the same pattern as
 * `alfie-context-claim`. Several registrations can be live at once (the dashboard and the ledger
 * overlay on top of it); the list is their union.
 *
 * MATCHING. Rules first (`ruleMatch`): exactly one topic named → that topic. Anything else goes to
 * the small AI pick (`finance-topic`), which chooses from the same list. Both only choose.
 */

export type FinanceTopicKind = 'figure' | 'key-question' | 'blocked' | 'account' | 'role';

export interface FinanceTopic {
  /** Unique across the screen: 'figure:gross-margin', 'kq:<id>', 'blocked', 'account:<guid>', 'role:<role>'. */
  id: string;
  kind: FinanceTopicKind;
  /** What the "About:" chip and the topic list show. */
  label: string;
  /**
   * Words that name this topic in a question, lower case. The label's own words are matched
   * too. Accounts list their name; never a bare code (a question full of amounts would hit it).
   */
  terms: string[];
  /** The brief, from the SAME builder the topic's ✦ button uses. Built on demand. */
  brief: () => string;
}

/**
 * Other ways builders name the dashboard figures. Keyed by metric id. Words for a ROLE ("running
 * costs", "debtors") live in `finance-role-topics`, not here, so they match the role, not the figure.
 */
export const FIGURE_TERMS: Record<string, string[]> = {
  'gross-margin': ['gross margin', 'gross profit', 'gp', 'gm'],
  'net-margin': ['net margin', 'net profit', 'bottom line'],
  'overhead-recovery': ['overhead recovery'],
  'cash-runway': ['cash runway', 'runway', 'how long will my cash last', 'cash last'],
  'debtor-days': ['debtor days', 'dso', 'getting paid'],
  'health-score': ['health score', 'financial health'],
};

/** Lower case, punctuation to spaces, single-spaced, padded so whole-word checks are a substring. */
function normalise(text: string): string {
  return ` ${text.toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9&]+/g, ' ').trim()} `;
}

/** The phrases that name a topic: its label (without anything in brackets) and its terms. */
function phrasesOf(t: FinanceTopic): string[] {
  const label = t.kind === 'account' ? t.label.replace(/^\s*\S+\s*·\s*/, '') : t.label;
  return [label, ...t.terms]
    .map((p) => normalise(p).trim())
    .filter((p) => p.length >= 2 && !(t.kind === 'account' && p.length < 4));
}

/**
 * The topics a question names by rule. One → use it. None or several → ask the AI pick.
 * An account also matches "account <code>" or "<code> <name>"; a bare number never does.
 */
export function ruleMatch(question: string, topics: FinanceTopic[]): FinanceTopic[] {
  const q = normalise(question);
  return topics.filter((t) => {
    if (phrasesOf(t).some((p) => q.includes(` ${p} `))) return true;
    if (t.kind === 'account') {
      const code = normalise(t.label.split('·')[0] ?? '').trim();
      if (code && q.includes(` account ${code} `)) return true;
    }
    return false;
  });
}

/* -------------------------------------------------------------------------- */
/* Registry — screens register while mounted, the widget reads                */
/* -------------------------------------------------------------------------- */

interface Registration {
  organizationId: string;
  getTopics: () => FinanceTopic[];
}

const registrations = new Map<string, Registration>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/**
 * Put the widget into finance mode for as long as this screen is mounted. `getTopics` is called at
 * the moment a question is matched, so it always reflects what is on screen. Returns the release,
 * which removes only this registration.
 */
export function registerFinanceScreen(
  id: string,
  organizationId: string,
  getTopics: () => FinanceTopic[] = () => [],
): () => void {
  registrations.set(id, { organizationId, getTopics });
  notify();
  return () => {
    if (registrations.get(id)?.getTopics === getTopics) {
      registrations.delete(id);
      notify();
    }
  };
}

/** True while a Xero screen is mounted. */
export const financeScreenActive = (): boolean => registrations.size > 0;

/** The organisation whose screen is up (all registrations are on the same one). */
export const financeOrganizationId = (): string | null =>
  registrations.values().next().value?.organizationId ?? null;

/** Every topic on screen right now, first registration wins on a duplicate id. */
export function financeTopics(): FinanceTopic[] {
  const out = new Map<string, FinanceTopic>();
  for (const r of registrations.values()) {
    for (const t of r.getTopics()) if (!out.has(t.id)) out.set(t.id, t);
  }
  return [...out.values()];
}

export function subscribeFinanceScreen(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* -------------------------------------------------------------------------- */
/* Per-conversation state — kept while the app is open, gone on reload        */
/* -------------------------------------------------------------------------- */

/** Conversations that were in finance mode, so /alfie can carry it. */
const financeThreads = new Set<string>();
/** Per conversation: the topic whose brief was last sent, so a follow-up does not re-send it. */
const briefSentFor = new Map<string, string>();

export const markFinanceThread = (threadId: string) => {
  financeThreads.add(threadId);
  notify();
};
export const isFinanceThread = (threadId: string | null | undefined): boolean =>
  !!threadId && financeThreads.has(threadId);
export const clearFinanceThread = (threadId: string) => {
  financeThreads.delete(threadId);
  notify();
};
export const briefSentForThread = (threadId: string | null | undefined): string | null =>
  (threadId && briefSentFor.get(threadId)) || null;
export const setBriefSentForThread = (threadId: string, topicId: string | null) => {
  if (topicId) briefSentFor.set(threadId, topicId);
  else briefSentFor.delete(threadId);
};

/** Tests only. */
export function __resetFinanceTopics(): void {
  registrations.clear();
  listeners.clear();
  financeThreads.clear();
  briefSentFor.clear();
}
